import { queryOptions } from '@tanstack/react-query';

import { ONE_DAY } from '@/constants/time';
import { type BatchReference, formatReferencesBatch } from '@/services/formatReferencesBatch';
import { toOpenableUrl } from '@/util/messageLinks';

/** What a link unfurls to: the page's own title and, for an article, where and when it ran. */
export type LinkPreview = {
  url: string;
  /** The host the reader will land on, without `www.`. */
  host: string;
  title: string;
  /** Journal and year for an article, else empty. */
  source: string;
  authors: string;
  /** The page's own `og:image`, an `https:` URL, for the thumbnail; null when it names none. */
  imageUrl: string | null;
};

export const linkPreviewKeys = {
  url: (url: string) => ['linkPreview', url] as const,
};

export function linkHost(url: string): string {
  try {
    return new URL(toOpenableUrl(url)).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * A preview only when the formatter read a real title, which it reports as
 * `resolved`. Its placeholder says nothing the link itself does not, so a card
 * built on it would only repeat the address, and like Slack the thread then
 * shows the plain link alone. An API too old to report it unfurls nothing.
 */
export function toLinkPreview(
  url: string,
  reference: BatchReference | undefined
): LinkPreview | null {
  const title = reference?.title.trim() ?? '';

  if (!reference?.resolved || !title) {
    return null;
  }

  const source = [reference.journal, reference.year].filter(Boolean).join(' · ');

  return {
    url,
    host: linkHost(reference.url || url),
    title,
    source,
    authors: reference.authors,
    imageUrl: reference.image_url?.startsWith('https://') ? reference.image_url : null,
  };
}

/**
 * Unfurls one link through the reference formatter the inquiry chat already
 * uses. It reads PubMed and PMC metadata from NCBI and any other page's title,
 * server side behind the SSRF guard, so the app never fetches a page itself.
 *
 * A page's title rarely changes, so a preview is kept for a day and never
 * retried: a failure leaves the plain link, which still works.
 */
export function getLinkPreviewQueryOptions(url: string) {
  const openableUrl = toOpenableUrl(url);

  return queryOptions({
    queryKey: linkPreviewKeys.url(openableUrl),
    queryFn: async () =>
      toLinkPreview(openableUrl, (await formatReferencesBatch([openableUrl]))[0]),
    staleTime: ONE_DAY,
    gcTime: ONE_DAY,
    retry: false,
  });
}
